import * as React from 'react';
import * as ProgressPrimitive from '@radix-ui/react-progress';
import { cn } from '@/lib/utils';

/**
 * Progress компонент для отображения прогресса с анимациями
 */
const Progress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> & {
    /** Показывать анимацию при достижении 100% */
    animateComplete?: boolean;
    /** Показывать пульсацию при изменении */
    showPulse?: boolean;
  }
>(({ className, value, animateComplete = true, showPulse = false, ...props }, ref) => {
  const [prevValue, setPrevValue] = React.useState(value);
  const [isAnimating, setIsAnimating] = React.useState(false);
  const [isComplete, setIsComplete] = React.useState(false);

  // Отслеживание изменений для анимации
  React.useEffect(() => {
    if (prevValue !== value) {
      // Если достигнут 100%
      if (value === 100 && prevValue !== 100 && animateComplete) {
        setIsComplete(true);
        const timer = setTimeout(() => setIsComplete(false), 1000);
        return () => clearTimeout(timer);
      }
      // Анимация изменения
      setIsAnimating(true);
      const timer = setTimeout(() => setIsAnimating(false), 500);
      setPrevValue(value);
      return () => clearTimeout(timer);
    }
  }, [value, prevValue, animateComplete]);

  return (
    <ProgressPrimitive.Root
      ref={ref}
      className={cn('relative h-4 w-full overflow-hidden rounded-full bg-secondary', className)}
      {...props}
    >
      <ProgressPrimitive.Indicator
        className={cn(
          'h-full w-full flex-1 bg-primary',
          'transition-[transform,background-color] duration-300 ease-out',
          isAnimating && showPulse && 'animate-milestone-pulse',
          isComplete && 'animate-progress-complete'
        )}
        style={{ transform: `translateX(-${100 - (value ?? 0)}%)` }}
      />
    </ProgressPrimitive.Root>
  );
});
Progress.displayName = ProgressPrimitive.Root.displayName;

export { Progress };
